import {
  monthOf,
  toYm,
  type TrendPoint,
} from '@/entities/performance/lib/performance';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

/** Figma 차트 색 — 처방(예상) 점선 · 정산(확정) 막대 */
export const CHART_COLOR = {
  line: '#0D8AFF',
  bar: '#c3eada',
  barActive: '#069054',
  unregistered: '#DBDBDB',
  grid: '#f1f1f1',
  future: '#f4f4f4',
} as const;

export type TrendMode = 'prescription' | 'settlement' | 'both';

interface Point {
  month: number;
  ym: string;
  estimated: number | null;
  confirmed: number | null;
  unregistered: boolean;
  future: boolean;
}

interface TrendChartProps {
  year: number;
  points: TrendPoint[];
  mode?: TrendMode;
  /** true면 1~12월을 모두 그리고 값이 없는 달은 "미도래" 음영 (M-08) */
  fullYear?: boolean;
  /** 진하게 칠할 달 (yyyyMM) */
  highlightYm?: string;
  height: number;
  barSize: number;
  tickSize?: number;
  /** 월을 누르면 호출 — 미도래 달은 호출하지 않는다 */
  onSelectMonth?: (ym: string) => void;
}

/**
 * 실적 금액 추이 (M-08) · 병의원 월별 추세 (M-09)
 * - 처방을 등록하지 않은 달은 회색 점만 찍는다
 * - 정산 막대는 confirmed 값이 있는 달만 그린다
 */
export const TrendChart = ({
  year,
  points,
  mode = 'both',
  fullYear,
  highlightYm,
  height,
  barSize,
  tickSize = 12,
  onSelectMonth,
}: TrendChartProps) => {
  const byYm = new Map(points.map((p) => [p.ym, p]));
  const months = fullYear
    ? Array.from({ length: 12 }, (_, i) => i + 1)
    : points.map((p) => monthOf(p.ym)).sort((a, b) => a - b);

  const data: Point[] = months.map((month) => {
    const ym = toYm(year, month);
    const p = byYm.get(ym);
    return {
      month,
      ym,
      estimated: p ? p.estimated : null,
      confirmed: p ? p.confirmed : null,
      unregistered: !!p?.unregistered,
      future: !p,
    };
  });

  const futureMonths = data.filter((d) => d.future);
  const showLine = mode !== 'settlement';
  const showBar = mode !== 'prescription';

  const select = (month?: number) => {
    const p = data.find((d) => d.month === month);
    if (p && !p.future) onSelectMonth?.(p.ym);
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart
        data={data}
        margin={{ top: 12, right: 0, bottom: 0, left: 0 }}
        onClick={(state) => select(state?.activeLabel as number | undefined)}
        style={{ cursor: onSelectMonth ? 'pointer' : undefined }}
      >
        <CartesianGrid vertical={false} stroke={CHART_COLOR.grid} />
        <YAxis hide domain={[0, 'auto']} />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          interval={0}
          tickMargin={10}
          tick={(props: {
            x: number;
            y: number;
            payload: { value: number };
          }) => {
            const p = data.find((d) => d.month === props.payload.value);
            const active = p?.ym === highlightYm && !fullYear;
            return (
              <text
                x={props.x}
                y={props.y}
                dy={tickSize}
                textAnchor="middle"
                fontSize={tickSize}
                fontWeight={active ? 700 : fullYear ? 500 : 400}
                fill={
                  p?.future
                    ? '#c6c6c6'
                    : active
                      ? '#111111'
                      : fullYear
                        ? '#555555'
                        : '#8d8d8d'
                }
                style={{
                  cursor:
                    p && !p.future && onSelectMonth ? 'pointer' : 'default',
                }}
                onClick={() => select(p?.month)}
              >
                {props.payload.value}월
              </text>
            );
          }}
        />
        {futureMonths.length > 0 && (
          <ReferenceArea
            x1={futureMonths[0].month}
            x2={futureMonths[futureMonths.length - 1].month}
            fill={CHART_COLOR.future}
            fillOpacity={0.85}
            stroke="none"
            radius={8}
            ifOverflow="extendDomain"
            label={{
              value: '미도래',
              fill: '#b8b8b8',
              fontSize: 12.5,
              fontWeight: 500,
            }}
          />
        )}
        {showBar && (
          <Bar
            dataKey="confirmed"
            maxBarSize={barSize}
            radius={[6, 6, 6, 6]}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell
                key={d.ym}
                fill={
                  d.ym === highlightYm ? CHART_COLOR.barActive : CHART_COLOR.bar
                }
              />
            ))}
          </Bar>
        )}
        {showLine && (
          <Line
            dataKey="estimated"
            stroke={CHART_COLOR.line}
            strokeWidth={2}
            strokeDasharray="6 5"
            isAnimationActive={false}
            connectNulls={false}
            dot={(props: { cx?: number; cy?: number; payload: Point }) => {
              const { cx, cy, payload } = props;
              if (
                cx === undefined ||
                cy === undefined ||
                payload.estimated === null
              )
                return <g key={payload.ym} />;
              return (
                <circle
                  key={payload.ym}
                  cx={cx}
                  cy={cy}
                  r={fullYear ? 5.5 : 5}
                  fill={
                    payload.unregistered
                      ? CHART_COLOR.unregistered
                      : CHART_COLOR.line
                  }
                />
              );
            }}
            activeDot={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
